<template>
  <div
    v-if="active"
    class="language-switcher dropdown is-select z-600"
    :class="[{ 'is-active': open }, 'is-' + props.direction]"
  >
    <div class="dropdown-trigger">
      <button class="button" @click="toggleDropdown">
        <div v-if="open">
          <div class="language-link">
            <span class="language-link__id uppercase">{{ active.code }}</span>
            <span class="language-link__title !overflow-visible !text-clip">{{
              active.label
            }}</span>
          </div>
        </div>
        <span v-else class="flex uppercase">
          <span>{{ active.code }}</span>
        </span>
      </button>
    </div>
    <div
      v-if="open"
      v-click-away="closeDropdown"
      class="dropdown-menu"
      role="menu"
    >
      <div class="dropdown-content">
        <ZtLink
          v-for="item in links"
          v-show="!item.active"
          :key="item.code"
          :to="item.to"
          class="language-link dropdown-item"
          :hreflang="item.code"
          @click="onClickLanguageLink(item.code)"
        >
          <span class="language-link__id uppercase">{{ item.code }}</span>
          <span class="language-link__title">{{ item.label }}</span>
          <!-- <span v-if="getAiGeneratedLanguage(item.id)" class="language-link__ai"> *</span> -->
        </ZtLink>

        <!-- <div class="dropdown-info">
          <div class="dropdown-info-text" v-html="aiDisclaimerText"></div>
        </div> -->
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { notNullish } from '@vueuse/core'

const props = withDefaults(
  defineProps<{
    direction?: string
  }>(),
  {
    direction: 'down',
  },
)

const open = ref(false)
const searchParams = (import.meta.client && window.location.search) || ''
const languageLinks = useLanguageLinks()
const route = useRoute()
const links = computed(() =>
  languageLinks.value
    ?.filter((link) => notNullish(link.to))
    .map((link) => ({
      ...link,
      // Named routes (e.g. book-public-tour/[nid]) come as objects — keep the
      // current path and only swap the language prefix. Avoids `[object Object]`.
      to:
        (typeof link.to === 'string'
          ? link.to
          : route.path.replace(/^\/[^/]+/, `/${link.code}`)) + searchParams,
    })),
)
const active = computed(
  () => links.value.length > 1 && links.value.find((v) => v.active),
)

const { trackEvent } = useAnalytics()

function toggleDropdown() {
  open.value = !open.value
  trackEvent({
    event: 'click_language_switcher',
    pageLanguage: '',
  })
}

function closeDropdown() {
  open.value = false
}

function onClickLanguageLink(pageLanguage: string) {
  closeDropdown()
  trackEvent({
    event: 'click_language_switcher',
    pageLanguage,
  })
}
</script>

<style lang="postcss">
.language-switcher {
  &.dropdown.is-select {
    .dropdown-trigger {
      .button {
        @apply h-auto rounded border border-grey-light py-[7px] pl-[8px] pr-[28px] text-size-6 text-grey-dark;
        line-height: normal;

        &:after {
          @apply right-[5px] bg-grey-dark text-[14px] leading-[1.5] text-grey-dark;
        }
      }
    }

    &:hover,
    &:focus {
      button {
        @apply text-grey-dark;
      }
    }

    &.is-active {
      .dropdown-trigger {
        .button {
          @apply pr-[5em] text-size-6 font-normal text-primary;
        }

        & .language-link {
          @apply flex justify-between;
        }
      }
      .dropdown-content {
        @apply border;
        border-bottom-left-radius: 4px;
        border-bottom-right-radius: 4px;

        .dropdown-item {
          @apply pl-[8px] text-size-6 text-grey-dark;

          &.is-active {
            @apply text-background;
          }

          &:first-child {
            @apply border-t-0;
          }
        }
      }

      &.is-up {
        .dropdown-content {
          border-top-left-radius: 4px;
          border-top-right-radius: 4px;
        }
      }
      &.is-down {
        .dropdown-content {
          border-bottom-left-radius: 4px;
          border-bottom-right-radius: 4px;
        }
      }
    }
    .language-link {
      &__id {
        @apply font-bold;
      }
      &__title {
        @apply ml-1;
      }
    }
  }
  .dropdown-info {
    @apply px-[8px] pb-[10px] pt-[12px];

    .dropdown-info-text {
      @apply pt-[8px] text-size-7;
      border-top: 1px solid $grey-light;
    }
  }
}
</style>
